// The legacy escape hatch's form (docs/18-one-fee-reserve.md 2.4, "A legacy device on a browser that fails the probe"
// and "The escape hatch's lifetime", step 1). DeviceDoor renders it after an unlock refused because this browser
// cannot hold the fee key, on a device that stores an older fee reserve envelope.
// - It offers "Send the older reserve out". The press begins the session's operation
//   (`session.beginLegacySendOut()`, legacy-send-out.ts) after this component has mounted, so React's development
//   remount (StrictMode) cannot cancel it; a second press while one runs does nothing.
// - It holds only the operation's handle: methods and public figures, never the seed, the signer or the wire.
// - The destination and the amount follow Send out's rule (5.1 A): "everything but the fee" only when the older balance
//   is at least F + 15,000, and a new destination receives at least F. One review panel (from, to, amount, fee), then
//   Confirm, then the device passphrase: no key exists during any user wait. The passphrase goes to the operation as an
//   argument and the field is cleared at once, as the door does after unlock (a JavaScript string cannot be zeroed).
// - A pre-R48 device (no recorded older address) opens the envelope once first, only to learn the address; Confirm
//   asks for the passphrase again.
// - Its Cancel and its unmount (navigation, Back or Forward, the door leaving the screen) cancel the operation
//   ('dismissed'). A cancel from elsewhere (a lock, the auto-lock, a Forget, the tab being hidden or closed) returns it
//   to the offer and says nothing was sent. After the send, a cancel lets the confirmation finish and the outcome is
//   shown while the form is mounted ("Already submitted", or "Failed on chain" when it landed and failed).
// - A recheck after the passphrase that finds changed figures ends that operation; the form begins a new one with the
//   same destination and amount and shows the review again, whose Confirm asks for the passphrase again.
// - The older address is labelled "older fee reserve" (OLDER_RESERVE_KIND), never like a top-up destination (docs/18
//   section 6, "After the sweep").

import { sendOutLimits } from '@thevault/sdk';
import { type FormEvent, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { LegacyFigures, LegacyPhase, LegacySendOutHandle } from '../legacy-send-out';
import { FEE_KEY_UNSUPPORTED_SENTENCE, session, useSession } from '../session';
import { explorerBase, explorerUrl, parseUnits, shortSig } from '../util';
import { OLDER_RESERVE_KIND } from './OlderReserve';
import { Address, Amount, Field, Kv, StatusLine } from './primitives';

const ENDED: ReadonlySet<LegacyPhase> = new Set<LegacyPhase>(['done', 'cancelled', 'failed']);

/** What the door says while a step runs; nothing secret is named. */
const WORKING: Partial<Record<LegacyPhase, string>> = {
  entry: 'Reading this device’s older fee reserve.',
  opening: 'Opening the older key to read its address.',
  reading: 'Reading the balances.',
  passphrase: 'Opening the older key.',
  importing: 'Opening the older key.',
  recheck: 'Checking the figures again.',
  blockhash: 'Preparing the transfer.',
  identity: 'Preparing the transfer.',
  signing: 'Signing the transfer.',
  simulating: 'Checking the transfer with the network.',
  sending: 'Sending.',
  confirming: 'Waiting for the network to confirm it.',
};

function Sol({ raw }: { raw: bigint }) {
  return <Amount raw={raw} decimals={9} symbol="SOL" maxFraction={9} />;
}

/** The handle's figures, re-read on every change it reports; null while no operation is held. */
function useFigures(handle: LegacySendOutHandle | null): LegacyFigures | null {
  const subscribe = useCallback((listener: () => void) => (handle ? handle.subscribe(listener) : () => {}), [handle]);
  return useSyncExternalStore(subscribe, () => (handle ? handle.figures() : null));
}

export function LegacySendOutForm({ onActiveChange }: { /** True while an operation is held: the door hides its passphrase form, which this form replaces. */ onActiveChange: (active: boolean) => void }) {
  const { settings } = useSession();
  const [handle, setHandle] = useState<LegacySendOutHandle | null>(null);
  const handleRef = useRef<LegacySendOutHandle | null>(null);
  const fig = useFigures(handle);
  const [destination, setDestination] = useState('');
  const [amountText, setAmountText] = useState('');
  const [everything, setEverything] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  // Confirm was pressed on the review: the passphrase field is shown (asked after Confirm, docs/18 2.4).
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [working, setWorking] = useState(false);
  const workingRef = useRef(false);
  const [inputError, setInputError] = useState<string | null>(null);
  // Shown at the offer after an operation ended without sending (a lock, a Forget, the tab hidden), or why a press was refused.
  const [note, setNote] = useState<{ word: string; text: string } | null>(null);
  // Shown above a review that a new operation opened after the recheck found changed figures.
  const [reviewNote, setReviewNote] = useState<string | null>(null);
  const onActive = useRef(onActiveChange);
  onActive.current = onActiveChange;

  // Unmount (navigation, Back or Forward, the door leaving the screen) cancels the operation held, if any. Mounted
  // before any press, so StrictMode's simulated unmount finds no handle.
  useEffect(
    () => () => {
      const h = handleRef.current;
      handleRef.current = null;
      if (h) {
        h.cancel('dismissed');
        onActive.current(false);
      }
    },
    [],
  );

  const drop = useCallback((after: { word: string; text: string } | null) => {
    handleRef.current = null;
    setHandle(null);
    setConfirming(false);
    setEditing(false);
    setPassphrase('');
    setReviewNote(null);
    setNote(after);
    onActive.current(false);
  }, []);

  // Cancelled from elsewhere before anything was sent: back to the offer, the door shows no operation.
  useEffect(() => {
    if (fig && fig.phase === 'cancelled' && !fig.submitted && handleRef.current === handle) drop(fig.stoppedBy === 'dismissed' ? null : { word: 'Stopped.', text: fig.error ?? 'Nothing was sent.' });
  }, [fig, handle, drop]);

  /**
   * Begin an operation (on a press), then its entry and reads. `again.from` carries the address an earlier operation
   * of this tab opened on a pre-R48 device (its Confirm must still open that key); with `again.destination` and
   * `again.amount`, the review is prepared for them.
   */
  const begin = async (again?: { from: string | null; destination?: string; amount?: bigint | 'everything' }) => {
    let h: LegacySendOutHandle;
    try {
      h = session.beginLegacySendOut();
    } catch (e) {
      setNote({ word: 'Not started.', text: e instanceof Error ? e.message : String(e) });
      return;
    }
    handleRef.current = h;
    setHandle(h);
    setNote(null);
    setConfirming(false);
    setEditing(false);
    setPassphrase('');
    setInputError(null);
    onActive.current(true);
    try {
      await h.open(again?.from ? { from: again.from } : undefined);
      if (again?.destination && again.amount !== undefined && h.figures().phase === 'review') await h.prepare(again.destination, again.amount);
    } catch {
      /* the figures carry the outcome, and the review shows a refused input */
    }
  };

  const offer = () => {
    if (handleRef.current) return;
    void begin();
  };

  const cancel = () => {
    const h = handleRef.current;
    if (!h) return;
    h.cancel('dismissed');
    const f = h.figures();
    // After the send the confirmation may finish: the form stays and shows the outcome (docs/18 2.4 step 5).
    if (f.submitted && !ENDED.has(f.phase)) return;
    drop(null);
  };

  const openAddress = async (e: FormEvent) => {
    e.preventDefault();
    const h = handleRef.current;
    if (!h || !passphrase || workingRef.current) return;
    const p = passphrase;
    setPassphrase('');
    workingRef.current = true;
    setWorking(true);
    try {
      await h.openAddress(p);
    } catch {
      /* shown from the figures */
    } finally {
      workingRef.current = false;
      setWorking(false);
    }
  };

  const amountInput = (): bigint | 'everything' | null => (everything ? 'everything' : parseUnits(amountText, 9));

  const review = async (e: FormEvent) => {
    e.preventDefault();
    const h = handleRef.current;
    if (!h || workingRef.current) return;
    const amount = amountInput();
    if (!destination.trim()) {
      setInputError('Enter the address to send to.');
      return;
    }
    if (amount === null || amount === 0n) {
      setInputError('Enter an amount in SOL, with up to nine decimals, or choose everything but the fee.');
      return;
    }
    setInputError(null);
    setReviewNote(null);
    workingRef.current = true;
    setWorking(true);
    try {
      await h.prepare(destination, amount);
      setEditing(false);
      setConfirming(false);
    } catch {
      /* a refused input stays in review with its sentence (figures.error) */
    } finally {
      workingRef.current = false;
      setWorking(false);
    }
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const h = handleRef.current;
    if (!h || !passphrase || workingRef.current) return;
    const p = passphrase;
    setPassphrase('');
    workingRef.current = true;
    setWorking(true);
    try {
      await h.confirm(p);
    } finally {
      workingRef.current = false;
      setWorking(false);
    }
    const f = h.figures();
    // The recheck found changed figures: that operation ended before signing. A new one reads afresh and shows the
    // review again; its Confirm asks for the passphrase again.
    if (handleRef.current === h && f.outcome?.kind === 'changed') {
      const reason = f.outcome.reason;
      const amount = amountInput();
      if (amount === null) return;
      await begin({ from: f.from, destination, amount });
      setReviewNote(reason);
    }
  };

  // A refusal ended the operation (step 5: trying again is a new operation): fresh reads, the same inputs once reviewed.
  const again = () => {
    const f = handleRef.current?.figures();
    const amount = amountInput();
    const reviewed = f !== undefined && f.amount !== null && destination.trim() !== '' && amount !== null;
    void begin({ from: f?.from ?? null, ...(reviewed ? { destination, amount } : {}) });
  };

  if (!handle || !fig) {
    return (
      <div className="door">
        {note ? (
          <StatusLine state={note.word === 'Not started.' ? 'failed' : 'neutral'} word={note.word}>
            {note.text}
          </StatusLine>
        ) : null}
        <p className="door-note">This device also holds an older fee reserve: a key from an earlier version of the app, sealed under the device passphrase. This browser can still send its SOL to an address you choose. Nothing else is unlocked.</p>
        <button type="button" className="cta cta-plain" onClick={offer}>
          Send the older reserve out
        </button>
      </div>
    );
  }

  const ended = ENDED.has(fig.phase);
  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const limits = fig.older !== null && fig.minimum !== null ? sendOutLimits(fig.older, fig.minimum, fig.fee) : null;

  const header = (
    <>
      <p className="door-note">{FEE_KEY_UNSUPPORTED_SENTENCE} The older fee reserve can still be sent out here: one transfer, signed by its own key, to the address you enter. Nothing else is unlocked, and nothing is stored.</p>
      {fig.from ? (
        <Kv
          rows={[
            ['Older fee reserve', <Address key="from" value={fig.from} kind={OLDER_RESERVE_KIND} />],
            ['It holds', fig.older !== null ? <Sol raw={fig.older} /> : 'Reading'],
          ]}
        />
      ) : null}
    </>
  );

  // The outcome: landed, not sent, changed (a new operation normally replaces this at once), landed and failed on
  // chain, or already submitted and not confirmed.
  if (ended && fig.outcome) {
    const o = fig.outcome;
    const sig = o.kind === 'landed' || o.kind === 'failed' || o.kind === 'submitted' ? o.signature : null;
    const link = sig !== null ? explorerUrl(txBase, sig) : null;
    return (
      <div className="door">
        {header}
        {o.kind === 'landed' ? (
          <StatusLine state="landed" word="Landed.">
            {fig.amount !== null ? <Sol raw={fig.amount} /> : 'The transfer'} went to {fig.to ? <span className="mono">{fig.to}</span> : 'the destination'}. Transaction {link ? <a href={link} target="_blank" rel="noreferrer">{shortSig(sig!)} ↗</a> : shortSig(sig!)}.
          </StatusLine>
        ) : o.kind === 'failed' || o.kind === 'submitted' ? (
          <StatusLine state="failed" word={o.kind === 'failed' ? 'Failed on chain.' : 'Already submitted.'}>
            {o.reason} Transaction {link ? <a href={link} target="_blank" rel="noreferrer">{shortSig(sig!)} ↗</a> : shortSig(sig!)}.
          </StatusLine>
        ) : (
          <StatusLine state="failed" word="Not sent.">
            {o.reason}
          </StatusLine>
        )}
        {o.kind === 'not-sent' || o.kind === 'changed' ? (
          <button type="button" className="cta cta-plain" onClick={again}>
            {o.kind === 'changed' ? 'Review again' : 'Try again'}
          </button>
        ) : null}
        <div className="forget-row">
          <button type="button" className="cta-quiet" onClick={() => drop(null)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // A step is running (or the operation was stopped after its send and waits for the confirmation).
  const step = WORKING[fig.phase];
  const userWait = fig.phase === 'review' || (fig.phase === 'opening' && fig.needsAddress && !working);
  if (!userWait && step) {
    return (
      <div className="door">
        {header}
        <StatusLine state="pending" word={fig.submitted ? 'Sent.' : 'Working.'}>
          {step}
          {fig.submitted && fig.stoppedBy ? ' Already submitted: the outcome is shown here when the network answers.' : ''}
        </StatusLine>
        <div className="forget-row">
          <button type="button" className="cta-quiet" onClick={cancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Pre-R48: the passphrase opens the envelope once, only to learn the older address; the key is closed again at once.
  if (fig.phase === 'opening' && fig.needsAddress) {
    return (
      <div className="door">
        {header}
        <form onSubmit={openAddress}>
          <p className="door-note">This device did not record the older fee reserve&rsquo;s address. Open it once with the device passphrase to read its address and balance; the key is closed again straight away and nothing is stored.</p>
          <Field id="legacy-open-passphrase" label="Device passphrase">
            <input id="legacy-open-passphrase" className="input" type="password" autoComplete="current-password" required value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
          </Field>
          <button type="submit" className="cta cta-mint" aria-disabled={working ? 'true' : undefined}>
            Open it to see its balance
          </button>
        </form>
        <div className="forget-row">
          <button type="button" className="cta-quiet" onClick={cancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const reviewed = fig.amount !== null && fig.to !== null && !editing;
  return (
    <div className="door">
      {header}
      {!reviewed ? (
        <form onSubmit={review}>
          {limits && fig.minimum !== null && fig.older !== null ? (
            limits.everything === null ? (
              <StatusLine state="failed" word="Nothing to send.">
                It holds less than its account minimum of <Sol raw={fig.minimum} /> plus the <Sol raw={fig.fee} /> fee, so nothing can be sent out of it.
              </StatusLine>
            ) : (
              <p className="door-note">
                Send everything but the <Sol raw={fig.fee} /> fee (<Sol raw={limits.everything} />), which empties it, or at most {limits.maxPartial !== null ? <Sol raw={limits.maxPartial} /> : 'nothing short of everything'}, which keeps its account minimum of <Sol raw={fig.minimum} />. A new address must receive at least <Sol raw={fig.minimum} />.
              </p>
            )
          ) : null}
          <Field id="legacy-destination" label="Destination address">
            <input id="legacy-destination" className="input" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Solana address" autoComplete="off" spellCheck={false} />
          </Field>
          <Field id="legacy-amount" label="Amount (SOL)">
            <input id="legacy-amount" className="input" inputMode="decimal" value={amountText} disabled={everything} onChange={(e) => setAmountText(e.target.value)} placeholder="0.00" autoComplete="off" />
          </Field>
          {limits?.everything !== null && limits !== null ? (
            <label className="checkbox-row">
              <input type="checkbox" checked={everything} onChange={(e) => setEverything(e.target.checked)} />
              <span>Everything but the fee</span>
            </label>
          ) : null}
          {inputError || fig.error ? (
            <StatusLine state="failed" word="Not allowed.">
              {inputError ?? fig.error}
            </StatusLine>
          ) : null}
          <button type="submit" className="cta cta-mint" aria-disabled={working || limits?.everything === null ? 'true' : undefined} disabled={limits?.everything === null}>
            {working ? 'Reading' : 'Review'}
          </button>
        </form>
      ) : (
        <>
          {reviewNote ? (
            <StatusLine state="neutral" word="Check again.">
              {reviewNote}
            </StatusLine>
          ) : null}
          <div className="review">
            <Kv
              rows={[
                ['From', <Address key="from" value={fig.from!} kind={OLDER_RESERVE_KIND} />],
                ['To', <Address key="to" value={fig.to!} />],
                ['Amount', fig.everything ? <>{<Sol raw={fig.amount!} />} (everything but the fee)</> : <Sol raw={fig.amount!} />],
                ['Network fee', <Sol key="fee" raw={fig.fee} />],
              ]}
            />
          </div>
          {!confirming ? (
            <>
              <button type="button" className="cta cta-mint" onClick={() => setConfirming(true)}>
                Confirm
              </button>
              <div className="forget-row">
                <button type="button" className="cta-quiet" onClick={() => setEditing(true)}>
                  Change
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={send}>
              <Field id="legacy-passphrase" label="Device passphrase" help="It opens the older key for this one transfer; the key is dropped as soon as the transfer is signed.">
                <input id="legacy-passphrase" className="input" type="password" autoComplete="current-password" required value={passphrase} onChange={(e) => setPassphrase(e.target.value)} aria-describedby="legacy-passphrase-help" />
              </Field>
              <button type="submit" className="cta cta-mint" aria-disabled={working ? 'true' : undefined}>
                Send
              </button>
            </form>
          )}
        </>
      )}
      <div className="forget-row">
        <button type="button" className="cta-quiet" onClick={cancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
